import { StatusBar } from "expo-status-bar";
import {
  Pressable,
  Platform,
  StyleSheet,
  ScrollView,
  Text,
  ActivityIndicator,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useState } from "react";
import { View } from "../components/Themed";
import { useQuery, useLazyQuery, gql, useMutation } from "@apollo/client";
import Config from "../constants/Config";
import { arrayBufferToBase64Url, base64UrlToArrayBuffer } from "../utils/base64url";

function readCborLength(view, offset, additionalInfo) {
  if (additionalInfo < 24) {
    return { length: additionalInfo, nextOffset: offset };
  }

  if (additionalInfo === 24) {
    return { length: view.getUint8(offset), nextOffset: offset + 1 };
  }

  if (additionalInfo === 25) {
    return { length: view.getUint16(offset), nextOffset: offset + 2 };
  }

  if (additionalInfo === 26) {
    return { length: view.getUint32(offset), nextOffset: offset + 4 };
  }

  if (additionalInfo === 27) {
    const high = view.getUint32(offset);
    const low = view.getUint32(offset + 4);
    const value = high * 2 ** 32 + low;
    if (!Number.isSafeInteger(value)) {
      throw new Error("CBOR value exceeds safe integer range");
    }
    return { length: value, nextOffset: offset + 8 };
  }

  throw new Error(`Unsupported CBOR additional info: ${additionalInfo}`);
}

function decodeHalfFloat(value) {
  const sign = (value & 0x8000) >> 15;
  const exponent = (value & 0x7c00) >> 10;
  const fraction = value & 0x03ff;

  if (exponent === 0) {
    return (sign ? -1 : 1) * 2 ** -14 * (fraction / 2 ** 10);
  }

  if (exponent === 0x1f) {
    if (fraction === 0) {
      return sign ? -Infinity : Infinity;
    }
    return NaN;
  }

  return (sign ? -1 : 1) * 2 ** (exponent - 15) * (1 + fraction / 2 ** 10);
}

function decodeCborItem(view, offset = 0) {
  const initialByte = view.getUint8(offset);
  offset += 1;

  const majorType = initialByte >> 5;
  const additionalInfo = initialByte & 0x1f;

  if (additionalInfo === 31) {
    throw new Error("Indefinite-length CBOR values are not supported");
  }

  if (majorType === 0 || majorType === 1) {
    const { length, nextOffset } = readCborLength(view, offset, additionalInfo);
    return {
      value: majorType === 0 ? length : -1 - length,
      nextOffset,
    };
  }

  if (majorType === 2 || majorType === 3) {
    const { length, nextOffset } = readCborLength(view, offset, additionalInfo);
    const byteStart = nextOffset;
    const byteEnd = byteStart + length;
    const bytes = new Uint8Array(view.buffer, view.byteOffset + byteStart, length);

    if (majorType === 2) {
      return {
        value: new Uint8Array(bytes),
        nextOffset: byteEnd,
      };
    }

    return {
      value: new TextDecoder("utf-8").decode(bytes),
      nextOffset: byteEnd,
    };
  }

  if (majorType === 4) {
    const { length, nextOffset } = readCborLength(view, offset, additionalInfo);
    let currentOffset = nextOffset;
    const arrayValue = [];

    for (let i = 0; i < length; i += 1) {
      const decoded = decodeCborItem(view, currentOffset);
      arrayValue.push(decoded.value);
      currentOffset = decoded.nextOffset;
    }

    return { value: arrayValue, nextOffset: currentOffset };
  }

  if (majorType === 5) {
    const { length, nextOffset } = readCborLength(view, offset, additionalInfo);
    let currentOffset = nextOffset;
    const mapValue = {};

    for (let i = 0; i < length; i += 1) {
      const keyDecoded = decodeCborItem(view, currentOffset);
      currentOffset = keyDecoded.nextOffset;
      const valueDecoded = decodeCborItem(view, currentOffset);
      currentOffset = valueDecoded.nextOffset;
      mapValue[keyDecoded.value] = valueDecoded.value;
    }

    return { value: mapValue, nextOffset: currentOffset };
  }

  if (majorType === 6) {
    const { nextOffset } = readCborLength(view, offset, additionalInfo);
    return decodeCborItem(view, nextOffset);
  }

  if (majorType === 7) {
    if (additionalInfo === 20) return { value: false, nextOffset: offset };
    if (additionalInfo === 21) return { value: true, nextOffset: offset };
    if (additionalInfo === 22) return { value: null, nextOffset: offset };
    if (additionalInfo === 23) return { value: undefined, nextOffset: offset };

    if (additionalInfo === 24) {
      return { value: view.getUint8(offset), nextOffset: offset + 1 };
    }

    if (additionalInfo === 25) {
      const half = view.getUint16(offset);
      return { value: decodeHalfFloat(half), nextOffset: offset + 2 };
    }

    if (additionalInfo === 26) {
      return { value: view.getFloat32(offset), nextOffset: offset + 4 };
    }

    if (additionalInfo === 27) {
      return { value: view.getFloat64(offset), nextOffset: offset + 8 };
    }
  }

  throw new Error(`Unsupported CBOR major type: ${majorType}`);
}

function extractCredentialPublicKeyFromAttestation(attestationObjectBuffer) {
  const attestationBytes = new Uint8Array(attestationObjectBuffer);
  const attestationView = new DataView(
    attestationBytes.buffer,
    attestationBytes.byteOffset,
    attestationBytes.byteLength
  );
  const decodedAttestation = decodeCborItem(attestationView);
  const authData = decodedAttestation.value?.authData;

  if (!(authData instanceof Uint8Array)) {
    throw new Error("Invalid attestation object: missing authData");
  }

  if (authData.length < 55) {
    throw new Error("Invalid authData length");
  }

  const flags = authData[32];
  const hasAttestedCredentialData = (flags & 0x40) !== 0;
  if (!hasAttestedCredentialData) {
    throw new Error("No attested credential data in authData");
  }

  let offset = 37; // rpIdHash(32) + flags(1) + signCount(4)
  offset += 16; // aaguid

  const credentialIdLength = (authData[offset] << 8) | authData[offset + 1];
  offset += 2 + credentialIdLength;

  const authDataView = new DataView(
    authData.buffer,
    authData.byteOffset,
    authData.byteLength
  );
  const decodedPublicKey = decodeCborItem(authDataView, offset);
  return authData.slice(offset, decodedPublicKey.nextOffset);
}

const PASSKEY_REGISTRATION_OPTIONS = gql`
  query passkeyRegistrationOptions($token: String!) {
    passkeyRegistrationOptions(token: $token) {
      challenge
      rpId
      rpName
      userId
      userName
      timeout
    }
  }
`;

const PASSKEYS_QUERY = gql`
  query passkeys($token: String!) {
    passkeys(token: $token) {
      id
      credentialId
      name
      createdAt
    }
  }
`;

const REGISTER_PASSKEY = gql`
  mutation registerPasskey(
    $token: String!
    $credentialId: String!
    $publicKey: String!
    $clientDataJSON: String!
    $attestationObject: String!
    $name: String
  ) {
    registerPasskey(
      token: $token
      credentialId: $credentialId
      publicKey: $publicKey
      clientDataJSON: $clientDataJSON
      attestationObject: $attestationObject
      name: $name
    )
  }
`;

const DELETE_PASSKEY = gql`
  mutation deletePasskey($token: String!, $passkeyId: ID!) {
    deletePasskey(token: $token, passkeyId: $passkeyId)
  }
`;

export default function OperatorSettingsPasskeysScreen() {
  const [token, setToken] = useState("");
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [passkeyError, setPasskeyError] = useState("");

  const { data: passkeysData, loading, refetch: refetchPasskeys } = useQuery(PASSKEYS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [getRegistrationOptions] = useLazyQuery(PASSKEY_REGISTRATION_OPTIONS);
  const [registerPasskeyMutation] = useMutation(REGISTER_PASSKEY);
  const [deletePasskeyMutation] = useMutation(DELETE_PASSKEY);

  useEffect(() => {
    async function fetchData() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}

      // Check if passkeys are supported
      if (typeof window !== 'undefined' && window.PublicKeyCredential) {
        setPasskeySupported(true);
      }
    }
    fetchData();
  }, []);

  async function registerPasskey() {
    setPasskeyError("");
    try {
      if (typeof navigator === "undefined" || !navigator.credentials?.create) {
        setPasskeyError("Passkey registration is not available on this device");
        return;
      }

      // Get registration options from server
      const optionsResult = await getRegistrationOptions({ variables: { token } });
      const options = optionsResult.data?.passkeyRegistrationOptions;

      if (!options) {
        setPasskeyError("Failed to get registration options");
        return;
      }

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: base64UrlToArrayBuffer(options.challenge),
          rp: {
            id: options.rpId,
            name: options.rpName,
          },
          user: {
            id: base64UrlToArrayBuffer(options.userId),
            name: options.userName,
            displayName: options.userName,
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },  // ES256
            { type: "public-key", alg: -257 }, // RS256
          ],
          timeout: options.timeout,
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "preferred",
            residentKey: "preferred",
          },
          attestation: "none",
        },
      });

      if (!credential) {
        setPasskeyError("Passkey creation cancelled");
        return;
      }

      // Extract the public key from the attestation response
      const response = credential.response;
      let publicKeyBuffer =
        typeof response.getPublicKey === "function" ? response.getPublicKey() : null;

      // Fallback for browsers that do not expose response.getPublicKey()
      if (!publicKeyBuffer && response.attestationObject) {
        try {
          const extracted = extractCredentialPublicKeyFromAttestation(response.attestationObject);
          publicKeyBuffer = extracted?.buffer || null;
        } catch (parseError) {
          console.warn("Passkey public key extraction fallback failed:", parseError);
        }
      }

      if (!publicKeyBuffer) {
        setPasskeyError("Could not extract passkey key data from the attestation response.");
        return;
      }

      // Send to server
      const result = await registerPasskeyMutation({
        variables: {
          token,
          credentialId: arrayBufferToBase64Url(credential.rawId),
          publicKey: arrayBufferToBase64Url(publicKeyBuffer),
          clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
          attestationObject: arrayBufferToBase64Url(response.attestationObject),
          name: null,
        },
      });

      if (result.data?.registerPasskey) {
        refetchPasskeys();
      } else {
        setPasskeyError("Failed to register passkey");
      }
    } catch (err) {
      console.error("Passkey registration error:", err);
      setPasskeyError(err.message || "Passkey registration failed");
    }
  }

  async function handleDeletePasskey(passkeyId) {
    try {
      const result = await deletePasskeyMutation({
        variables: { token, passkeyId },
      });
      if (result.data?.deletePasskey) {
        refetchPasskeys();
      }
    } catch (err) {
      console.error("Delete passkey error:", err);
      setPasskeyError(err.message || "Failed to delete passkey");
    }
  }

  if (!passkeySupported) {
    return (
      <ScrollView>
        <View style={styles.container}>
          <View style={styles.card}>
            <Text style={styles.title}>Passkeys</Text>
            <Text style={styles.description}>
              Passkeys are not supported on this device or browser.
            </Text>
          </View>
        </View>
      </ScrollView>
    );
  }

  if (!token) {
    return (
      <ScrollView>
        <View style={styles.container}>
          <View style={styles.card}>
            <Text style={styles.title}>Passkeys</Text>
            <Text style={styles.description}>
              Please log in to manage your passkeys.
            </Text>
          </View>
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView>
      <View style={styles.container}>
        <View style={styles.card}>
          <Text style={styles.title}>Passkeys</Text>
          <Text style={styles.description}>
            Passkeys let you sign in quickly and securely using your device's biometrics (fingerprint, face recognition) or screen lock.
          </Text>

          {loading ? (
            <ActivityIndicator size="large" color={Config.companyColor || '#007AFF'} style={{ marginTop: 20 }} />
          ) : (
            <>
              {passkeysData?.passkeys?.length > 0 ? (
                <View style={styles.passkeyList}>
                  <Text style={styles.sectionTitle}>Your Passkeys</Text>
                  {passkeysData.passkeys.map((pk) => (
                    <View key={pk.id} style={styles.passkeyItem}>
                      <View style={styles.passkeyInfo}>
                        <Text style={styles.passkeyName}>
                          {pk.name || 'Passkey'}
                        </Text>
                        <Text style={styles.passkeyDate}>
                          Added {new Date(pk.createdAt).toLocaleDateString()}
                        </Text>
                      </View>
                      <Pressable
                        onPress={() => handleDeletePasskey(pk.id)}
                        style={styles.removeButton}
                      >
                        <Text style={styles.removeButtonText}>Remove</Text>
                      </Pressable>
                    </View>
                  ))}
                </View>
              ) : (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>
                    No passkeys registered yet
                  </Text>
                </View>
              )}

              <Pressable
                style={styles.addButton}
                onPress={registerPasskey}
              >
                <Text style={styles.addButtonText}>
                  Add Passkey
                </Text>
              </Pressable>

              {passkeyError ? (
                <Text style={styles.errorText}>{passkeyError}</Text>
              ) : null}
            </>
          )}
        </View>

        <View style={styles.infoCard}>
          <Text style={styles.infoTitle}>About Passkeys</Text>
          <Text style={styles.infoText}>
            Passkeys are a safer and easier replacement for passwords. They use your device's built-in security features like Touch ID, Face ID, or Windows Hello.
          </Text>
          <Text style={[styles.infoText, { marginTop: 10 }]}>
            Your passkey is stored securely on your device and can be synced across your devices through your platform's password manager (iCloud Keychain, Google Password Manager, etc.).
          </Text>
        </View>

        <StatusBar style={Platform.OS === "ios" ? "light" : "auto"} />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  passkeyList: {
    marginTop: 20,
  },
  passkeyItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#f8f8f8',
    borderRadius: 8,
    marginBottom: 8,
  },
  passkeyInfo: {
    flex: 1,
  },
  passkeyName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#333',
  },
  passkeyDate: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  removeButton: {
    padding: 8,
    marginLeft: 12,
  },
  removeButtonText: {
    color: '#ff3b30',
    fontSize: 14,
    fontWeight: '500',
  },
  emptyState: {
    marginTop: 20,
    padding: 20,
    backgroundColor: '#f8f8f8',
    borderRadius: 8,
    alignItems: 'center',
  },
  emptyStateText: {
    color: '#888',
    fontSize: 14,
  },
  addButton: {
    marginTop: 20,
    padding: 14,
    backgroundColor: '#007AFF',
    borderRadius: 8,
    alignItems: 'center',
  },
  addButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  errorText: {
    color: '#ff3b30',
    marginTop: 12,
    textAlign: 'center',
  },
  infoCard: {
    backgroundColor: '#f0f7ff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 13,
    color: '#555',
    lineHeight: 18,
  },
});
